import { skipToken, useQuery } from '@tanstack/react-query';
import React from 'react';

import { getOrganizationRootContentId } from '@gitbook/app-hive';
import { useHiveClient } from '@gitbook/hive-react';
import { HTTPError, Logger } from '@gitbook/util-debug';
import {
    type AgentOrganizationRpc,
    hasChangeRequestMergeEvent,
} from '@services/content-agents/browser';

import { apiQuery } from '../../state';
import {
    type GitBookAgentClient,
    type GitBookAgentClientOptions,
    type GitBookAgentClientState,
    GitBookAgentClientStatus,
} from './GitBookAgentClient';
import { agentIsIdle, useAgent, useAgentState, useDropAgent } from './useAgent';
import {
    deleteOrganizationConversation,
    provisionOrganizationConversation,
} from './useOrganizationConversations';

const logger = Logger('app:agent-dock');

/**
 * Where the user currently is, reported to the agent so it can resolve an unqualified "this site"
 * without searching the organization for it.
 *
 * Deliberately coarse — site, space, change request, and no page. The page reaches the agent on the
 * messages that are about one, so walking through a space does not send an update for something no
 * message will ask about.
 */
export type OrganizationAgentLocation = {
    /** Path the user is on, so the agent can read what the ids leave out. */
    url?: string;
    siteId?: string;
    spaceId?: string;
    /** Set by the full-screen agent screen, which offers follow-up suggestions. */
    surface?: 'agent_screen';
    /**
     * The change request as the URL addresses it: its number in the space, or — where a link was
     * built from one — its id. Carried as it was read, since everything it is handed to resolves
     * either.
     */
    changeRequest?: string | number;
};

type OrganizationAgentParams = {
    organizationId: string;
    conversationId: string;
};

/**
 * Build the websocket pathname for an organization agent conversation.
 *
 * The conversation and nothing else. This is the connection pool's key and the key the client
 * stores its outbox under — so anything else on here forks a conversation in two: navigating
 * would mint a second client for the same durable object and leave a message that never sent
 * stranded under wherever the user was standing when they wrote it. Where the user is goes over RPC
 * instead, which is a property of the moment rather than of the connection.
 */
export function getOrganizationAgentPathname({
    organizationId,
    conversationId,
}: Pick<OrganizationAgentParams, 'organizationId' | 'conversationId'>) {
    return `/agents/o/${organizationId}/agent/conversation/${conversationId}`;
}

/**
 * Connect to an organization agent conversation.
 *
 * The conversation must already have been provisioned through the API, which is what gives the
 * agent the token it acts with — connecting to an unprovisioned id yields an agent whose tools all
 * fail.
 */
export function useOrganizationAgent(
    input: OrganizationAgentParams &
        Pick<GitBookAgentClientOptions, 'transcript'> & {
            enabled?: boolean;
        }
): GitBookAgentClient<AgentOrganizationRpc> | null {
    const { organizationId, conversationId, transcript } = input;
    const hiveClient = useHiveClient();
    const enabled = input.enabled ?? true;

    const getToken = React.useCallback(
        () => hiveClient.auth.getToken(getOrganizationRootContentId(organizationId)),
        [hiveClient, organizationId]
    );

    // On every connection, since re-provisioning is how the agent's expiring token is re-issued.
    // Only a turn spends that token, and turns start from deliveries, which wait for this; the
    // socket does not. Watchers skip it: nothing acts through them.
    const prepareConnection = React.useCallback(
        async (connection: { transcript: boolean }) => {
            if (!connection.transcript) {
                return;
            }
            await provisionOrganizationConversation({ organizationId, conversationId }).catch(
                (error) => logger.error('Failed to provision the agent conversation', error)
            );
        },
        [organizationId, conversationId]
    );

    return useAgent<AgentOrganizationRpc>({
        pathname: getOrganizationAgentPathname({ organizationId, conversationId }),
        getToken,
        prepareConnection,
        enabled,
        transcript,
    });
}

/**
 * Tell a conversation where the user is, for as long as it is the one in front of them.
 *
 * Called by the surface showing the conversation rather than by the hook that connects to it: a
 * conversation the user is only watching from a tab is not the one they are in, and one client
 * serves both, so where the user is follows what is on screen and not what the connection
 * receives. Reporting from every surface holding a client would send a frame per navigation to
 * every conversation in the dock, waking each durable object and costing it a write.
 *
 * Reported whenever it changes, and again on every (re)connection: the durable object holds the
 * last value it was told, and a fresh object — or one woken from hibernation into a new RPC
 * session — has only what it persisted.
 */
export function useReportAgentLocation(input: {
    agent: GitBookAgentClient<AgentOrganizationRpc> | null;
    location: OrganizationAgentLocation;
}): void {
    const { agent } = input;
    const { url, siteId, spaceId, changeRequest, surface } = input.location;
    const connected = useAgentState(agent, agentIsConnected);

    React.useEffect(() => {
        // Only over a live session. Reporting into a socket that is connecting, or gone, throws and
        // tells the agent nothing; the reconnection puts this back to true and sends it then.
        if (!agent || !connected) {
            return;
        }

        void agent.actions
            .setLocation({
                ...(url ? { url } : {}),
                ...(siteId ? { siteId } : {}),
                ...(spaceId ? { spaceId } : {}),
                // The agent stores what it is told under `changeRequestId`, and resolves a number
                // reported that way like any other identifier.
                ...(changeRequest !== undefined ? { changeRequestId: String(changeRequest) } : {}),
                ...(surface ? { surface } : {}),
            })
            .catch((error) => {
                // Costs the agent its sense of place, not the conversation: an unqualified "this
                // site" goes unresolved, while everything the user names outright still works.
                logger.error('Failed to report the location to the agent', error);
            });
    }, [agent, connected, url, siteId, spaceId, changeRequest, surface]);
}

/**
 * Have the agent show in the conversation that its change request was merged when that happened
 * outside it, so the conversation reads the same as when the agent merged it.
 */
export function useRecordChangeRequestMerged(input: {
    agent: GitBookAgentClient<AgentOrganizationRpc> | null;
    changeRequestId: string | undefined;
    merged: boolean;
}): void {
    const { agent, changeRequestId, merged } = input;
    const idle = useAgentState(agent, agentIsIdle);
    const shown = useAgentState(agent, (state: GitBookAgentClientState) =>
        merged && changeRequestId
            ? hasChangeRequestMergeEvent(state.messages, changeRequestId)
            : false
    );

    React.useEffect(() => {
        // Idle only: a turn that merges it shows the merge itself.
        if (!agent || !idle || !merged || shown) {
            return;
        }

        void agent.actions.recordChangeRequestMerged().catch((error) => {
            logger.error('Failed to record the merged change request', error);
        });
    }, [agent, idle, merged, shown]);
}

/**
 * The change request the agent works in, when it works in one. Neither retried nor thrown: it only
 * decorates the conversation, so a failure leaves it as if there were none.
 */
export function useAgentEditTargetChangeRequest(
    editTarget: { spaceId: string; changeRequestId?: string | null } | null | undefined
) {
    return useQuery({
        ...apiQuery(
            'getChangeRequestById',
            editTarget?.changeRequestId
                ? { spaceId: editTarget.spaceId, changeRequestId: editTarget.changeRequestId }
                : skipToken
        ),
        retry: false,
        throwOnError: false,
    });
}

/**
 * Delete an organization agent conversation. Callers handle what follows, like closing its tab.
 */
export function useDeleteOrganizationConversation(organizationId: string) {
    const dropAgent = useDropAgent();
    return React.useCallback(
        async (conversationId: string) => {
            // Dropped before the request, not after: deleting destroys the durable object, which
            // closes the socket, and a client still in the pool reads that as a connection to
            // re-establish. Left there it reconnects and re-provisions the id it was told to forget.
            dropAgent(getOrganizationAgentPathname({ organizationId, conversationId }));

            try {
                await deleteOrganizationConversation({ organizationId, conversationId });
            } catch (error) {
                if (!(error instanceof HTTPError && error.statusCode === 404)) {
                    throw error;
                }
            }
        },
        [dropAgent, organizationId]
    );
}

function agentIsConnected(state: { status: GitBookAgentClientStatus }): boolean {
    return (
        state.status === GitBookAgentClientStatus.IDLE ||
        state.status === GitBookAgentClientStatus.PROCESSING
    );
}

/**
 * Build the websocket pathname for a background agent of an organization conversation.
 */
export function getOrganizationBackgroundAgentPathname({
    organizationId,
    conversationId,
}: Pick<OrganizationAgentParams, 'organizationId' | 'conversationId'>) {
    return `/agents/o/${organizationId}/agent/background/${conversationId}`;
}

/**
 * Connect to a background agent's own conversation, to read what it did. Never provisioned: the
 * conversation that started it handed it its credentials.
 */
export function useOrganizationBackgroundAgent(
    input: OrganizationAgentParams &
        Pick<GitBookAgentClientOptions, 'transcript'> & {
            enabled?: boolean;
        }
): GitBookAgentClient<AgentOrganizationRpc> | null {
    const { organizationId, conversationId, transcript } = input;
    const hiveClient = useHiveClient();
    const enabled = input.enabled ?? true;

    const getToken = React.useCallback(
        async () => hiveClient.auth.getToken(getOrganizationRootContentId(organizationId)),
        [hiveClient, organizationId]
    );

    return useAgent<AgentOrganizationRpc>({
        pathname: getOrganizationBackgroundAgentPathname({ organizationId, conversationId }),
        getToken,
        enabled,
        transcript,
    });
}
